import React, { Suspense, useState } from 'react';
import { useAuth } from '../../../../context/AuthContext';
import { Link } from 'react-router-dom';
import {
  BookOpen,
  BarChart2,
  User,
  Send,
  GraduationCap,
  Users,
  MessageCircle,
  X,
  Globe,
} from 'lucide-react';
import { motion } from 'framer-motion';
import Loader from '../../../../components/common/Loader';
import ChatRoom from '../../../../components/common/ChatRoom'; // ✅ adjust path where your ChatRoom is stored

const AdminDashboard: React.FC = () => {
  const { user, isAuthenticated, isLoading } = useAuth();
  const [isChatOpen, setIsChatOpen] = useState(false);

  if (isLoading) {
    return (
      <div className="flex min-h-64 items-center justify-center bg-gray-100">
        <Loader />
      </div>
    );
  }

  if (!isAuthenticated || !user || !user.isAdmin) {
    return (
      <p className="text-center mt-20 text-lg text-red-600 font-semibold">
        Access Denied: Admins Only.
      </p>
    );
  }

  return (
    <Suspense fallback={<Loader />}>
      <div className="relative space-y-8 bg-gray-100 p-4 sm:p-6 lg:p-10">
        {/* Animated Heading */}
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
          className="text-2xl sm:text-3xl font-bold text-gray-800"
        >
          Welcome to Admin Dashboard, {user.name} 👋
        </motion.h1>

        {/* Dashboard Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
          {/* Manage Mentors */}
          <Link
            to="/dashboard/admin/mentors"
            className="p-6 bg-white rounded-xl shadow-md ring-1 ring-gray-200 hover:shadow-lg hover:scale-[1.02] transition-transform duration-300 flex flex-col"
          >
            <Users className="w-7 h-7 text-purple-600 mb-3" />
            <h3 className="text-lg font-semibold text-gray-800">Manage Mentors</h3>
            <p className="text-sm text-gray-500">
              Oversee all mentor accounts and permissions.
            </p>
          </Link>

          {/* Manage Courses */}
          <Link
            to="/dashboard/admin/manage-courses"
            className="p-6 bg-white rounded-xl shadow-md ring-1 ring-gray-200 hover:shadow-lg hover:scale-[1.02] transition-transform duration-300 flex flex-col"
          >
            <BookOpen className="w-7 h-7 text-blue-600 mb-3" />
            <h3 className="text-lg font-semibold text-gray-800">Manage Courses</h3>
            <p className="text-sm text-gray-500">
              View, edit, or delete all course content.
            </p>
          </Link>

          {/* Create Blog */}
          <Link
            to="/dashboard/admin/create-blog"
            className="p-6 bg-white rounded-xl shadow-md ring-1 ring-gray-200 hover:shadow-lg hover:scale-[1.02] transition-transform duration-300 flex flex-col"
          >
            <Send className="w-7 h-7 text-indigo-600 mb-3" />
            <h3 className="text-lg font-semibold text-gray-800">Create Blog</h3>
            <p className="text-sm text-gray-500">Write and publish new blog posts.</p>
          </Link>

          {/* Manage Enrollments */}
          <Link
            to="/dashboard/admin/manage-enrollments"
            className="p-6 bg-white rounded-xl shadow-md ring-1 ring-gray-200 hover:shadow-lg hover:scale-[1.02] transition-transform duration-300 flex flex-col"
          >
            <GraduationCap className="w-7 h-7 text-orange-600 mb-3" />
            <h3 className="text-lg font-semibold text-gray-800">Manage Enrollments</h3>
            <p className="text-sm text-gray-500">
              Oversee student course enrollments.
            </p>
          </Link>

          {/* Students List */}
          <Link
            to="/dashboard/admin/students"
            className="p-6 bg-white rounded-xl shadow-md ring-1 ring-gray-200 hover:shadow-lg hover:scale-[1.02] transition-transform duration-300 flex flex-col"
          >
            <User className="w-7 h-7 text-green-600 mb-3" />
            <h3 className="text-lg font-semibold text-gray-800">All Students</h3>
            <p className="text-sm text-gray-500">
              View and manage all student accounts.
            </p>
          </Link>

          {/* Analytics */}
          <div className="p-6 bg-white rounded-xl shadow-md ring-1 ring-gray-200 hover:shadow-lg hover:scale-[1.02] transition-transform duration-300 flex flex-col">
            <BarChart2 className="w-7 h-7 text-red-600 mb-3" />
            <h3 className="text-lg font-semibold text-gray-800">Analytics</h3>
            <p className="text-sm text-gray-500">
              View performance and platform data (coming soon).
            </p>
          </div>

          {/* Manage Pages */}
          <Link
            to="/dashboard/admin/manage-pages"
            className="p-6 bg-white rounded-xl shadow-md ring-1 ring-gray-200 hover:shadow-lg hover:scale-[1.02] transition-transform duration-300 flex flex-col"
          >
            <Globe className="w-7 h-7 text-teal-600 mb-3" />
            <h3 className="text-lg font-semibold text-gray-800">Manage Pages</h3>
            <p className="text-sm text-gray-500">
              Customize Home, About Us, Contact Us, and Forum content dynamically.
            </p>
          </Link>
        </div>

        {/* Floating Chat Button */}
        <button
          onClick={() => setIsChatOpen(!isChatOpen)}
          className="fixed bottom-6 right-6 bg-blue-600 text-white p-4 rounded-full shadow-lg hover:bg-blue-700 transition"
        >
          {isChatOpen ? <X size={24} /> : <MessageCircle size={24} />}
        </button>

        {/* ChatRoom Popup */}
        {isChatOpen && (
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed bottom-20 right-6 w-[350px] sm:w-[400px] h-[500px] shadow-xl rounded-2xl overflow-hidden border bg-white"
          >
            <ChatRoom roomName="admin-room" />
          </motion.div>
        )}
      </div>
    </Suspense>
  );
};

export default AdminDashboard;
